/* eslint-disable react/no-danger */
import classNames from 'classnames';
import { isBefore, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

// import * as AppPropTypes from '../../lib/PropTypes';
import styles from '../../../styles/partials/timer.module.scss';

const propTypes = {
    className: PropTypes.string,
    separatorClassName: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    delay: PropTypes.number,
    started: PropTypes.bool,
    blinkSeparator: PropTypes.bool,
    initialTimeRemaining: PropTypes.arrayOf(PropTypes.string),
    showDigitsOnEnded: PropTypes.bool,
    onEnded: PropTypes.func,
};

const defaultProps = {
    className: null,
    separatorClassName: null,
    date: null,
    delay: null, // seconds
    started: false,
    blinkSeparator: false,
    initialTimeRemaining: null,
    showDigitsOnEnded: false,
    onEnded: null,
};

function Timer({
    className,
    separatorClassName,
    date,
    delay,
    started,
    blinkSeparator,
    initialTimeRemaining,
    showDigitsOnEnded,
    onEnded,
}) {
    const parseTime = useCallback((deltaSeconds) => {
        let delta = deltaSeconds;
        const days = Math.floor(delta / 86400);
        delta -= days * 86400;
        const hours = Math.floor(delta / 3600) % 24;
        delta -= hours * 3600;
        const minutes = Math.floor(delta / 60) % 60;
        delta -= minutes * 60;
        const seconds = Math.floor(delta % 60);

        return [
            { unit: 'days', count: days },
            { unit: 'hours', count: hours },
            { unit: 'minutes', count: minutes },
            { unit: 'seconds', count: seconds },
        ]
            .filter(({ count }, countIndex) => count > 0 || countIndex > 1)
            .map(({ count, ...rest }) => ({
                ...rest,
                label: `${count}`.padStart(2, '0'),
            }));
    }, []);

    const newDate = useMemo(() => {
        const parsedDate = typeof date === 'string' ? parseISO(date) : date;
        if (date !== null && isBefore(new Date(), parsedDate)) {
            return parsedDate;
        }

        if (delay > 0) {
            const delayDate = new Date();
            delayDate.setTime(delayDate.getTime() + delay * 1000);
            return delayDate;
        }

        return null;
    }, [date, delay, started]);

    const [timeRemaining, setTimeRemaining] = useState(initialTimeRemaining);

    useEffect(() => {
        let interval = null;

        if (started) {
            if (newDate !== null) {
                const updateTime = () => {
                    const now = new Date();
                    const diffSeconds = (newDate.getTime() - now.getTime()) / 1000;

                    if (diffSeconds < 0) {
                        if (interval !== null) {
                            clearInterval(interval);
                        }
                        interval = null;
                        if (onEnded !== null) {
                            onEnded(newDate);
                        }
                    } else {
                        setTimeRemaining(parseTime(diffSeconds));
                    }
                };

                interval = setInterval(updateTime, 250);
                updateTime(newDate);
            } else if (onEnded !== null) {
                onEnded(newDate);
            }
        } else if (newDate !== null) {
            const now = new Date();
            const diffSeconds = delay > 0 ? delay : (newDate.getTime() - now.getTime()) / 1000;
            setTimeRemaining(parseTime(diffSeconds));
        }

        return () => {
            if (interval !== null) {
                clearInterval(interval);
                interval = null;
            }
        };
    }, [delay, newDate, started, onEnded]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.started]: started,
                    [styles.blinkSeparator]: blinkSeparator,
                },
            ])}
        >
            {(timeRemaining || []).map(({ unit, label }, unitIndex) => (
                <span key={`unit-${unit}`} className={styles.unit}>
                    <span className={styles.label}>{label}</span>
                    {unitIndex < timeRemaining.length - 1 ? (
                        <span
                            className={classNames([
                                styles.separator,
                                { [separatorClassName]: separatorClassName !== null },
                            ])}
                        >
                            :
                        </span>
                    ) : null}
                </span>
            ))}
            {showDigitsOnEnded && (timeRemaining === null || timeRemaining.length === 0) ? (
                <>
                    <span className={styles.unit}>
                        <span className={styles.label}>00</span>
                        <span
                            className={classNames([
                                styles.separator,
                                { [separatorClassName]: separatorClassName !== null },
                            ])}
                        >
                            :
                        </span>
                    </span>
                    <span className={styles.unit}>
                        <span className={styles.label}>00</span>
                    </span>
                </>
            ) : null}
        </div>
    );
}

Timer.propTypes = propTypes;
Timer.defaultProps = defaultProps;

export default Timer;
